import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/api';

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  login: (token: string) => void;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  token: null,
  login: () => {},
  logout: () => {},
  refreshToken: async () => false,
});

export const useAuth = () => useContext(AuthContext);

// Session timeout in milliseconds (30 minutes)
const SESSION_TIMEOUT = 30 * 60 * 1000;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const apiBase = getApiUrl();
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!token);
  const [lastActivity, setLastActivity] = useState<number>(Date.now());

  // Use refs to avoid circular dependencies
  const tokenRef = useRef(token);
  const lastActivityRef = useRef(lastActivity);

  // Update refs when state changes
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    lastActivityRef.current = lastActivity;
  }, [lastActivity]);

  // Login function - STABLE with useCallback
  const login = useCallback((newToken: string) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setIsAuthenticated(true);
    setLastActivity(Date.now());
  }, []);

  // Logout function - STABLE with useCallback
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setIsAuthenticated(false);
    setLastActivity(0);
    
    // Redirect to login page
    window.location.href = '/login';
  }, []);

  // Check if session has expired - STABLE with useCallback
  const checkSessionExpiry = useCallback(() => {
    if (tokenRef.current && Date.now() - lastActivityRef.current > SESSION_TIMEOUT) {
      console.log('Session expired due to inactivity');
      // Call logout directly without dependency
      localStorage.removeItem('token');
      setToken(null);
      setIsAuthenticated(false);
      setLastActivity(0);
      window.location.href = '/login';
      return true;
    }
    return false;
  }, []);

  // Refresh token - STABLE with useCallback
  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (!tokenRef.current) return false;
    
    try {
      const response = await axios.post(`${apiBase}/auth/refresh`, {}, {
        headers: { Authorization: `Bearer ${tokenRef.current}` }
      });
      
      if (response.data.access_token) {
        // Call login directly without dependency
        localStorage.setItem('token', response.data.access_token);
        setToken(response.data.access_token);
        setIsAuthenticated(true);
        setLastActivity(Date.now());
        return true;
      }
      return false;
    } catch (error) {
      console.log('Token refresh failed:', error);
      return false;
    }
  }, []);

  // Update last activity timestamp - STABLE with useCallback
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
  }, []);

  // Set up activity listeners
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      updateActivity();
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [updateActivity]);

  // Set up session expiry check
  useEffect(() => {
    const sessionCheckInterval = setInterval(() => {
      checkSessionExpiry();
    }, 60000); // Check every minute

    return () => clearInterval(sessionCheckInterval);
  }, [checkSessionExpiry]);

  // Set up axios interceptors
  useEffect(() => {
    // Request interceptor - add token to requests
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle authentication errors
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // Handle 401 Unauthorized errors
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          // Try to refresh the token
          const refreshSuccess = await refreshToken();
          
          if (refreshSuccess) {
            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          } else {
            // Refresh failed, logout user
            logout();
            return Promise.reject(error);
          }
        }

        // Do not logout on 403 — forbidden means lack of privilege, not invalid session
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [token, logout, refreshToken]);



  return (
    <AuthContext.Provider value={{ isAuthenticated, token, login, logout, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
}; 